import * as React from 'react';
import { observer } from 'mobx-react-lite';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { routes } from '../../../config/routes';
import { Navigation } from './navigation/header-navigation';
import { Account } from './account/header-account';
import { Container } from '../../../components/web/container/container';
import { Logo } from '../../../components/app/logo/logo';
import { cn } from '../../../utils/tailwind';
import { IconMenu } from '../../../components/app/icons/menu';
import { IconClose } from '../../../components/app/icons/close';
import { IconNext } from '../../../components/app/icons/next';
import styles from './header.module.css';

type HeaderType = {
    inverse?: boolean;
};

export const Header: React.FC<HeaderType> = observer(({ inverse = false }) => {
    const { t } = useTranslation();
    const router = useRouter();
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const [isMenuMounted, setIsMenuMounted] = React.useState(false);
    const backdropRef = React.useRef<HTMLDivElement>(null);
    const pendingAuthAction = React.useRef<(() => void) | null>(null);
    const menuRef = React.useRef<HTMLDivElement>(null);
    const menuButtonRef = React.useRef<HTMLButtonElement>(null);

    React.useEffect(() => {
        if (isMenuMounted || !pendingAuthAction.current) return undefined;
        const timer = window.setTimeout(() => {
            const action = pendingAuthAction.current;
            pendingAuthAction.current = null;
            action?.();
        }, 0);
        return () => window.clearTimeout(timer);
    }, [isMenuMounted]);

    React.useEffect(() => {
        const backdrop = backdropRef.current;
        if (!backdrop) return undefined;

        let frame = 0;
        let previousOpacity = -1;
        const updateOpacity = () => {
            frame = 0;
            // Follow scroll position instead of restarting a transition around one threshold.
            const opacity = Math.min(1, Math.max(0, window.scrollY / 64));
            if (opacity !== previousOpacity) {
                backdrop.style.opacity = String(opacity);
                previousOpacity = opacity;
            }
        };
        const onScroll = () => {
            if (!frame) frame = window.requestAnimationFrame(updateOpacity);
        };
        updateOpacity();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.cancelAnimationFrame(frame);
        };
    }, []);

    React.useEffect(() => {
        setIsMenuOpen(false);
    }, [router.asPath]);

    React.useEffect(() => {
        if (isMenuOpen) {
            setIsMenuMounted(true);
            return undefined;
        }
        const timer = window.setTimeout(() => setIsMenuMounted(false), 300);
        return () => window.clearTimeout(timer);
    }, [isMenuOpen]);

    React.useEffect(() => {
        if (!isMenuOpen || !isMenuMounted) return undefined;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        menuRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
        return () => {
            document.body.style.overflow = previousOverflow;
            menuButtonRef.current?.focus();
        };
    }, [isMenuOpen, isMenuMounted]);

    React.useEffect(() => {
        const desktop = window.matchMedia('(min-width: 1100px)');
        const closeOnDesktop = () => {
            if (desktop.matches) setIsMenuOpen(false);
        };
        desktop.addEventListener('change', closeOnDesktop);
        return () => desktop.removeEventListener('change', closeOnDesktop);
    }, []);

    const mobileNavigationItems = [
        {
            href: routes.web.index,
            label: t('pages.layout.header.home'),
            active: router.pathname === routes.web.index,
        },
        {
            href: routes.web.pricing,
            label: t('pages.layout.header.pricing'),
            active: router.pathname === routes.web.pricing,
        },
        {
            href: routes.web.compoundInterest,
            label: t('pages.layout.header.calculator'),
            active: router.pathname === routes.web.compoundInterest || router.pathname === '/compound-interest',
        },
        {
            href: routes.web.faq,
            label: t('pages.layout.header.faq'),
            active: router.pathname === routes.web.faq,
        },
        {
            href: routes.web.contact,
            label: t('pages.layout.header.contact'),
            active: router.pathname === routes.web.contact,
        },
    ];

    React.useEffect(() => {
        if (!isMenuOpen) {
            return undefined;
        }

        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as Node;

            if (menuRef.current?.contains(target) || menuButtonRef.current?.contains(target)) {
                return;
            }

            setIsMenuOpen(false);
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Tab') {
                const elements = menuRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
                const visible = Array.from(elements || []).filter((element) => element.getClientRects().length > 0);
                const first = visible[0];
                const last = visible[visible.length - 1];
                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault();
                    last?.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault();
                    first?.focus();
                }
            }
            if (event.key === 'Escape') {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isMenuOpen]);

    return (
        <>
            <div
                ref={backdropRef}
                aria-hidden="true"
                className={cn(
                    'pointer-events-none fixed inset-x-0 top-0 z-[109] h-[13rem] backdrop-blur-[8px]',
                    inverse ? 'bg-brand-secondary/90' : 'bg-[#fdf7f2]/90',
                )}
                style={{ opacity: 0, maskImage: 'linear-gradient(to bottom, black 0%, black 38%, transparent 100%)' }}
            />
            <div
                data-web-header
                className="pointer-events-auto fixed inset-x-0 top-0 z-[110] isolate flex h-[9rem] w-full flex-row items-center justify-between py-[2rem]"
            >
                <Container>
                    <div className="relative flex items-center justify-between gap-[1.6rem]">
                        <Logo
                            href={routes.web.index}
                            inverse={inverse}
                            gradientId="web-header-logo"
                            compactOnMobile={false}
                        />

                        <Navigation inverse={inverse} />

                        <div className="flex items-center gap-xsmall">
                            <div className="hidden md:block">
                                <Account inverse={inverse} />
                            </div>
                            <button
                                ref={menuButtonRef}
                                type="button"
                                className={cn(
                                    'relative z-[120] flex h-[4.2rem] w-[4.2rem] items-center justify-center rounded-full border text-brand-secondary transition-colors min-[1100px]:hidden [&_i]:h-[1.8rem] [&_i]:w-[1.8rem]',
                                    inverse
                                        ? 'border-neutral-white/30 text-neutral-white'
                                        : 'border-border text-brand-secondary',
                                )}
                                aria-label={isMenuOpen ? 'Zavřít menu' : 'Otevřít menu'}
                                aria-expanded={isMenuOpen}
                                aria-controls="web-mobile-navigation"
                                onClick={() => setIsMenuOpen((value) => !value)}
                            >
                                {isMenuOpen ? <IconClose /> : <IconMenu />}
                            </button>
                        </div>

                        {isMenuMounted && (
                            <>
                                <div
                                    aria-hidden="true"
                                    data-open={isMenuOpen}
                                    className={cn(
                                        styles.menuBackdrop,
                                        'fixed inset-0 z-[125] bg-brand-secondary/15 backdrop-blur-[4px] min-[1100px]:hidden',
                                    )}
                                />
                                <div
                                    id="web-mobile-navigation"
                                    role="dialog"
                                    aria-modal="true"
                                    aria-label="Menu"
                                    data-open={isMenuOpen}
                                    ref={menuRef}
                                    className={cn(
                                        styles.menuPanel,
                                        'pointer-events-auto fixed inset-y-0 right-0 z-[130] flex h-[100dvh] w-full max-w-[46rem] flex-col overflow-y-auto border-l border-[#d9cdc5] bg-[#fdf7f2] px-[2.4rem] pb-[max(2.4rem,env(safe-area-inset-bottom))] shadow-[0_16px_48px_rgba(38,29,88,0.10)] min-[1100px]:hidden',
                                    )}
                                >
                                    <div className="flex min-h-[9rem] shrink-0 items-center justify-between">
                                        <Logo gradientId="web-sidebar-logo" compactOnMobile={false} />
                                        <button
                                            type="button"
                                            aria-label="Zavřít navigaci"
                                            onClick={() => setIsMenuOpen(false)}
                                            className="flex h-[4.2rem] w-[4.2rem] items-center justify-center rounded-full border border-[#d9cdc5] text-brand-secondary [&_i]:h-[1.8rem] [&_i]:w-[1.8rem]"
                                        >
                                            <IconClose />
                                        </button>
                                    </div>
                                    <nav
                                        aria-label="Hlavní navigace"
                                        className="mb-[3.2rem] mt-[2rem] flex flex-col text-[2.8rem] font-medium tracking-[-0.03em] text-brand-secondary"
                                    >
                                        {mobileNavigationItems.map((item, index) => (
                                            <Link key={item.href} href={item.href}>
                                                <a
                                                    style={{ animationDelay: `${100 + index * 55}ms` }}
                                                    aria-current={item.active ? 'page' : undefined}
                                                    onClick={() => setIsMenuOpen(false)}
                                                    className={cn(
                                                        styles.menuItem,
                                                        'group flex min-h-[7.2rem] items-center justify-between gap-[1.6rem] border-b border-[#d9cdc5]/60 py-[1.6rem] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-primary',
                                                        item.active ? 'text-brand-primary' : 'hover:text-brand-primary',
                                                    )}
                                                >
                                                    <span>{item.label}</span>
                                                    <span
                                                        aria-hidden="true"
                                                        className={cn(
                                                            'flex h-[3.2rem] w-[3.2rem] shrink-0 items-center justify-center rounded-full [&_i]:h-[1.4rem] [&_i]:w-[1.4rem]',
                                                            item.active
                                                                ? 'bg-brand-primary/10 text-brand-primary'
                                                                : 'text-web-copy/60 group-hover:text-brand-secondary',
                                                        )}
                                                    >
                                                        <IconNext />
                                                    </span>
                                                </a>
                                            </Link>
                                        ))}
                                    </nav>
                                    <div className="mt-auto pt-[2rem] [&>div]:w-full [&>div]:flex-col [&>div]:gap-[1rem] [&>div>*]:w-full">
                                        <Account
                                            expanded
                                            onAuthAction={(action) => {
                                                pendingAuthAction.current = action;
                                                setIsMenuOpen(false);
                                            }}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </Container>
            </div>
        </>
    );
});
